import { CONTENT_STATUS_HELP, CONTENT_STATUS_LONG_LABELS, type ContentStatus } from '@nova/shared';
import { ArrowRight, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { Drawer } from '../../components/Drawer.js';

const FLOW: { status: ContentStatus; next: string }[] = [
  { status: 'SUGGESTED', next: 'Produktansvarlig vurderer' },
  { status: 'APPROVED', next: 'Nova skriver, eller ansvarlig skriver selv' },
  { status: 'DRAFT', next: 'Ansvarlig redigerer og planlegger' },
  { status: 'SCHEDULED', next: 'Ansvarlig publiserer' },
  { status: 'PUBLISHED', next: 'Ferdig' },
];

/**
 * Five statuses is not many, but the flow is not obvious from the names alone —
 * particularly that a suggestion and a text are approved at different points.
 * Rather than explain it in onboarding nobody reads, it sits one click from the
 * plan itself (dokumentasjon/07-utviklingsplan.md 2.1.4).
 */
export function FlowExplainer() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-ink-soft hover:text-ink inline-flex items-center gap-1.5 text-xs transition"
      >
        <HelpCircle size={14} aria-hidden />
        Slik fungerer flyten
      </button>

      <Drawer open={open} onClose={() => setOpen(false)} title="Slik fungerer flyten">
        <div className="space-y-4 p-5">
          <p className="text-ink-soft text-sm leading-relaxed">
            Alt innhold går gjennom de samme fem stegene. Nova gjør det operative — foreslår,
            skriver, klargjør og purrer — mens et menneske eier hver overgang. Ingenting går ut uten
            at noen har sagt ja.
          </p>

          <ol className="space-y-2">
            {FLOW.map(({ status, next }, index) => (
              <li
                key={status}
                className="border-hairline bg-panel/40 rounded-panel border px-3.5 py-3"
              >
                <div className="flex items-baseline gap-2">
                  <span className="text-ink-soft num-tabular text-[11px]">{index + 1}</span>
                  <span className="text-ink text-sm font-medium">
                    {CONTENT_STATUS_LONG_LABELS[status]}
                  </span>
                </div>
                <p className="text-ink-soft mt-1 pl-6 text-xs leading-relaxed">
                  {CONTENT_STATUS_HELP[status]}
                </p>
                <p className="text-ink-soft/70 mt-1.5 flex items-center gap-1.5 pl-6 text-[11px]">
                  <ArrowRight size={11} aria-hidden />
                  {next}
                </p>
              </li>
            ))}
          </ol>

          <p className="text-ink-soft/70 text-xs leading-relaxed">
            Et forslag kan avvises, og en sak kan kanselleres underveis. Begge deler krever en
            begrunnelse, og begge havner i aktivitetsloggen.
          </p>
        </div>
      </Drawer>
    </>
  );
}
